import type { PropertyFilters } from '@/types/property'

export function countActivePropertyFilters(
  filters: PropertyFilters,
  options?: { ignoreIntent?: boolean; ignoreCategory?: boolean; ignoreArea?: boolean },
): number {
  let count = 0
  if (!options?.ignoreCategory && (filters.category?.length ?? 0) > 0) count++
  if ((filters.bhk?.length ?? 0) > 0) count++
  if (!options?.ignoreArea && (filters.area?.length ?? 0) > 0) count++
  if (filters.budgetMin != null || filters.budgetMax != null) count++
  if ((filters.saleType?.length ?? 0) > 0) count++
  if (filters.verified) count++
  if (filters.premium) count++
  if (filters.hot) count++
  if (!options?.ignoreIntent && filters.intent) count++
  return count
}

export function clearAllPropertyFilters(): Partial<PropertyFilters> {
  return {
    intent: undefined,
    area: [],
    category: [],
    verified: false,
    premium: false,
    hot: false,
    bhk: [],
    saleType: [],
    budgetMin: undefined,
    budgetMax: undefined,
    sort: 'relevance',
  }
}

export const SORT_OPTIONS = [
  { value: 'relevance', label: 'Relevance' },
  { value: 'newest', label: 'Newest' },
  { value: 'price_asc', label: 'Price: Low to High' },
  { value: 'price_desc', label: 'Price: High to Low' },
] as const

export function getSortLabel(sort?: PropertyFilters['sort']) {
  return SORT_OPTIONS.find((o) => o.value === (sort ?? 'relevance'))?.label ?? 'Sort'
}

export function toggleListValue(list: string[] | undefined, value: string) {
  const current = list ?? []
  return current.includes(value) ? current.filter((item) => item !== value) : [...current, value]
}

function norm(value: string) {
  return value.trim().toLowerCase().replace(/[-_]+/g, ' ').replace(/\s+/g, ' ')
}

function equivalents(value: string): string[] {
  const n = norm(value)
  const out = new Set<string>([n])

  if (n.endsWith('s')) out.add(n.slice(0, -1))
  else out.add(`${n}s`)

  const aliases: Record<string, string[]> = {
    shop: ['shops', 'showroom'],
    shops: ['shop', 'showroom'],
    house: ['houses'],
    houses: ['house'],
    flat: ['flats', 'apartment'],
    flats: ['flat', 'apartment'],
    plot: ['plots', 'land'],
    plots: ['plot', 'land'],
    office: ['offices'],
    offices: ['office'],
  }

  ;(aliases[n] ?? []).forEach((a) => out.add(norm(a)))
  return Array.from(out)
}

export function hasListValueNormalized(list: string[] | undefined, option: string) {
  if (!list?.length) return false
  const optionForms = new Set(equivalents(option))
  return list.some((item) => equivalents(item).some((f) => optionForms.has(f)))
}

export function toggleListValueNormalized(list: string[] | undefined, value: string) {
  const current = list ?? []
  const valueForms = new Set(equivalents(value))
  const filtered = current.filter((item) => !equivalents(item).some((f) => valueForms.has(f)))
  const removed = filtered.length !== current.length
  return removed ? filtered : [...filtered, value]
}

export function normalizeAreaText(value: string) {
  return value
    .trim()
    .toLowerCase()
    .replace(/^(?:by|near|at|the|in)\s+/i, '')
    .replace(/\b(feet|foot)\b/g, 'ft')
    .replace(/[-_]+/g, ' ')
    .replace(/[^\w\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
}

function collapseDoubledLetters(value: string) {
  return value.replace(/(.)\1+/g, '$1')
}

function fuzzyTokenMatch(a: string, b: string) {
  if (a === b) return true

  const ca = collapseDoubledLetters(a)
  const cb = collapseDoubledLetters(b)
  if (ca === cb) return true

  // Short tokens ("ag") must never mid-match longer words ("pragati").
  const minLen = Math.min(ca.length, cb.length)
  if (minLen < 3) return false

  // Prefer prefix matches (aaji → aji / mav → mavdi) over mid-string.
  if (ca.startsWith(cb) || cb.startsWith(ca)) return true

  // Mid-string contains only for longer tokens (avoid "ag" ⊂ "pragati").
  if (minLen >= 4 && (ca.includes(cb) || cb.includes(ca))) return true

  return false
}

/** Score how well a free-text area query matches a known locality name. */
export function areaMatchScore(query: string, area: string) {
  const q = normalizeAreaText(query)
  const a = normalizeAreaText(area)
  if (!q || !a) return 0
  if (q === a) return 100

  const qCompact = q.replace(/\s/g, '')
  const aCompact = a.replace(/\s/g, '')
  if (qCompact === aCompact) return 95

  // Whole-query containment only when query is long enough to be specific.
  // "Nehru Nagar Main Road" must not freely match shorter "Nehru Nagar" when
  // leftover tokens are property-type words (showroom/flat/…) from a title.
  if (q.length >= 4 && a.length >= 4 && a.includes(q)) return 80
  if (q.length >= 4 && a.length >= 4 && q.includes(a) && q !== a) {
    const leftover = q.replace(a, ' ').replace(/\s+/g, ' ').trim()
    const leftoverTokens = leftover.split(' ').filter(Boolean)
    const noise = new Set([
      'main', 'road', 'marg', 'street', 'near', 'at', 'on', 'in', 'the', 'by',
      'touch', 'ft', 'feet', 'foot', 'ring', 'opp', 'opposite', 'beside',
    ])
    const typeWord = /^(showroom|shops?|offices?|flats?|houses?|apartments?|villas?|tenements?|bungalows?|plots?|land|bhk|rent|sale|sell|commercial|residential)$/i
    const meaningful = leftoverTokens.filter((t) => !noise.has(t) && t.length > 1)
    if (meaningful.some((t) => typeWord.test(t))) return 0
    if (meaningful.length === 0) return 72
    if (meaningful.length >= 2) return 35
    return 48
  }

  const qTokens = q.split(' ').filter(Boolean)
  const aTokens = a.split(' ').filter(Boolean)

  if (
    qTokens.length > 0 &&
    qTokens.every((qt) => aTokens.some((at) => fuzzyTokenMatch(qt, at)))
  ) {
    return 70 + qTokens.length * 5
  }

  let overlap = 0
  qTokens.forEach((qt) => {
    if (aTokens.some((at) => fuzzyTokenMatch(qt, at))) overlap++
  })
  if (overlap >= 2) return overlap * 10
  if (overlap === 1 && qTokens.length === 1 && qTokens[0].length >= 3) return 55

  return 0
}

export function matchAreaToFilterOption(raw: string, options: string[], minScore = 55): string | null {
  const query = raw.trim()
  if (!query || !options.length) return null

  const best = options
    .map((name) => ({ name, score: areaMatchScore(query, name) }))
    .sort((a, b) => b.score - a.score)[0]

  return best && best.score >= minScore ? best.name : null
}

export function matchCategoryToFilterOption(raw: string, options: string[]): string | null {
  const query = raw.trim()
  if (!query || !options.length) return null

  const direct = options.find((name) => hasListValueNormalized([query], name))
  if (direct) return direct

  const aliases: Record<string, string[]> = {
    plots: ['Residential Land or Plot', 'plot', 'plots', 'land'],
    plot: ['Residential Land or Plot', 'plot', 'plots', 'land'],
    land: ['Residential Land or Plot', 'Agricultural Land'],
    house: ['Houses', 'house', 'houses'],
    houses: ['Houses', 'house', 'houses'],
    flat: ['Flats', 'flat', 'flats', 'apartment'],
    flats: ['Flats', 'flat', 'flats', 'apartment'],
    shop: ['Shops', 'shop', 'shops', 'showroom'],
    shops: ['Shops', 'shop', 'shops'],
    office: ['Offices', 'office', 'offices'],
    offices: ['Offices', 'office', 'offices'],
    warehouse: ['Shed or Warehouse', 'warehouse', 'shed'],
    'new project': ['New Project'],
    'new projects': ['New Project'],
    commercial: ['Commercial Projects', 'Commercial Land or Plot'],
  }

  const needle = query.toLowerCase()
  const candidates = aliases[needle] ?? [query]
  for (const candidate of candidates) {
    const hit = options.find((name) => hasListValueNormalized([candidate], name))
    if (hit) return hit
  }

  // Fuzzy contains: "residential plot" → "Residential Land or Plot"
  const compact = needle.replace(/\s+/g, ' ')
  const fuzzy = options.find((name) => {
    const n = name.toLowerCase()
    return n.includes(compact) || compact.split(' ').every((t) => t.length > 2 && n.includes(t))
  })
  return fuzzy ?? null
}

function compareAreaNames(a: string, b: string) {
  return a.localeCompare(b, 'en', { sensitivity: 'base', numeric: true })
}

/** Selected areas first, then A–Z within each group. */
export function sortAreaFilterOptions<T extends { id?: number; name: string }>(
  areas: readonly T[],
  selectedAreas: string[] | undefined,
): T[] {
  return [...areas].sort((a, b) => {
    const aValue = a.id && a.id > 0 ? String(a.id) : a.name
    const bValue = b.id && b.id > 0 ? String(b.id) : b.name
    const aSelected =
      hasListValueNormalized(selectedAreas, aValue) || hasListValueNormalized(selectedAreas, a.name)
    const bSelected =
      hasListValueNormalized(selectedAreas, bValue) || hasListValueNormalized(selectedAreas, b.name)
    if (aSelected !== bSelected) return aSelected ? -1 : 1
    return compareAreaNames(a.name, b.name)
  })
}
